import React, { useEffect, useState } from "react";
import { IonInput, useIonToast } from "@ionic/react";
import { Controller, useForm } from "react-hook-form";
import { CustomBottomSheet } from "../../components/UI/CustomBottomSheet";
import { useGetProfileQuery, useUpdateProfileMutation } from "../../providers/profile";
import { useAuth } from "../../contexts/auth-context";
import classNames from "classnames/bind";
import styles from "./UserProfileEditor.module.scss";
import { User } from "@core/types";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

// Field configuration type
export interface ProfileFieldConfig {
  name: keyof Omit<User, "discountCardNumber" | "token" | "id" | "supabaseAuth">;
  label: string;
  icon?: string;
  type?: string;
  required?: boolean;
  rules?: Record<string, any>;
  helperText?: string[];
  initialValue?: string;
  render?: (props: { value: string; onChange: (value: string) => void; disabled: boolean }) => React.ReactElement;
}

interface UserProfileEditorProps {
  isOpen?: boolean;
  onClose?: () => void;
  field: ProfileFieldConfig;
  previewContent?: React.ReactNode;
  onOpenBottomSheet?: () => void;
  disabled?: boolean;
}

const UserProfileEditor: React.FC<UserProfileEditorProps> = ({
  isOpen = false,
  onClose,
  field,
  previewContent,
  onOpenBottomSheet,
  disabled = false,
}) => {
  const { user } = useAuth();
  const userId = user?.id || "";
  const { t } = useTranslation();
  const { data: fetchedProfile } = useGetProfileQuery({
    userId: userId,
    options: {
      enabled: !!userId,
    },
  });

  const [open, setOpen] = useState(isOpen);
  const [presentToast] = useIonToast();
  const { updateUserData } = useAuth();
  const { mutate: updateProfile } = useUpdateProfileMutation();

  const { control, handleSubmit, setValue } = useForm<Record<ProfileFieldConfig["name"], string>>({
    mode: "onSubmit",
    defaultValues: {
      [field.name]: field.initialValue || "",
    },
  });

  // Update form when initial values change
  useEffect(() => {
    if (field.initialValue !== undefined) {
      setValue(field.name, field.initialValue);
    }
  }, [field, setValue]);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    onOpenBottomSheet?.();
  };

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  const onSubmit = (formData: Record<ProfileFieldConfig["name"], string>) => {
    if (!userId) return;

    const userPayload = {
      ...fetchedProfile,
      ...formData,
    };
    console.log({ fetchedProfile, formData });

    updateProfile(
      {
        userId,
        userPayload: userPayload,
      },
      {
        onError: () => {
          presentToast({
            message: t("Error Updating Data!"),
            color: "danger",
            duration: 2000,
          });
        },
        onSuccess: () => {
          handleClose();
          updateUserData({
            ...user,
            firstName: formData.firstName,
          });
          presentToast({
            message: t("Data successfully updated"),
            color: "success",
            duration: 2000,
          });
        },
      },
    );
  };

  // Edit a single field in the bottom sheet
  const renderFieldEditor = () => {
    if (!field) return null;

    return (
      <div className="flex flex-col items-center gap-1 px-4 pt-24 w-full">
        <div className="font-bold text-gray-400">{field.label}</div>
        <Controller
          control={control}
          name={field.name}
          rules={field.rules || { required: field.required }}
          render={({ field: { onChange, value } }) => {
            if (field.render) {
              return field.render({
                value: value as string,
                onChange,
                disabled: false,
              });
            }
            return (
              <IonInput
                autofocus
                type={(field.type || "text") as any}
                value={value}
                onIonChange={(e) => onChange(e.detail.value)}
                className={cx("input", "font-bold text-[1.875rem] text-center rounded-md w-full")}
              />
            );
          }}
        />
        {field.helperText && (
          <div className="flex flex-col items-center gap-1">
            {field.helperText.map((text, index) => (
              <div key={index} className="text-sm text-gray-400 text-center">
                {text}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <CustomBottomSheet
      open={open}
      onDismiss={handleClose}
      showDefaultHeader={false}
      contentClassName={cx("bottom_sheet", "flex flex-col items-center")}
      snapPoints={({ minHeight }) => [minHeight + 200, minHeight + 200]}
      renderPreview={() => (
        <div onClick={handleOpen} role="button" tabIndex={0}>
          {previewContent}
        </div>
      )}
      footer={
        <button onClick={handleSubmit(onSubmit)} className="w-full font-bold bg-primary">
          {t("Save")}
        </button>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>{renderFieldEditor()}</form>
    </CustomBottomSheet>
  );
};

export default UserProfileEditor;
